import { Link } from "react-router-dom";
import { ChevronRight, Mail, MapPin } from "lucide-react";
import { FacebookIcon, InstagramIcon } from "./BrandIcons";
import { scrollToHashTarget } from "@/lib/scrollToHash";
import logo from "@/assets/logo-toraflex.png";
import logoSaiph from "@/assets/logo-saiph.png";


interface FooterProps {
  onOpenSidebar: () => void;
}

export default function Footer({ onOpenSidebar }: FooterProps) {
  const links = [
    { href: "/historia", label: "Nossa História" },
    { href: "/produtos", label: "Produtos" },
    { href: "/#depoimentos", label: "Depoimentos" },
    { href: "/#faq", label: "FAQ" },
    { href: "/trabalhe-conosco", label: "Trabalhe Conosco" },
  ];

  const socialLinks = [
    { icon: <FacebookIcon className="w-5 h-5" />, href: "https://www.facebook.com/toraflex", label: "Facebook da Toraflex" },
    { icon: <InstagramIcon className="w-5 h-5" />, href: "https://www.instagram.com/toraflexcolchoes/", label: "Instagram da Toraflex" },
  ];

  const contactItems = [
    { icon: <Mail className="w-4 h-4" />, text: "vendas@toraflex.com.br", href: "mailto:vendas@toraflex.com.br" },
    { icon: <MapPin className="w-4 h-4" />, text: "Betim, MG — Brasil", href: "https://maps.app.goo.gl/BGnJuuWJ22CvFbYk8" },
  ];

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (!href.includes("#") || window.location.pathname !== "/") return;

    const hash = `#${href.split("#")[1]}`;
    e.preventDefault();
    window.history.replaceState(null, "", `/`);
    scrollToHashTarget(hash);
  };

  return (
    <footer className="bg-slate-950 text-white relative z-10">
      <div className="container mx-auto px-6 py-14">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center md:text-left">
          <div className="flex flex-col gap-5 items-center md:items-start">
            <Link to="/">
              <img src={logo} alt="Toraflex Colchões" className="h-10 w-auto object-contain brightness-0 invert" width={160} height={40} loading="lazy" />
            </Link>
            <p className="text-slate-300 text-sm leading-relaxed max-w-xs">
              Há mais de 40 anos fabricando colchões com qualidade superior e entregando o melhor para lojistas de todo o Brasil.
            </p>
            <div className="flex gap-1 mt-1 justify-center md:justify-start">
              {socialLinks.map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label} className="text-slate-400 hover:text-white transition-colors duration-200 min-w-[48px] min-h-[48px] flex items-center justify-center">
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-heading font-bold text-white text-sm uppercase tracking-wider mb-5">Navegação</h2>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-3 items-start">
              {links.map((l) => (
                <li key={l.href}>
                  <Link
                    to={l.href}
                    onClick={(e) => handleLinkClick(e, l.href)}
                    className="text-slate-300 hover:text-white text-sm transition-colors duration-200 inline-flex items-center gap-2 group min-h-[44px]"
                  >
                    <ChevronRight className="w-3 h-3 text-brand-red opacity-0 group-hover:opacity-100 transition-opacity" />
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-heading font-bold text-white text-sm uppercase tracking-wider mb-5">Contato</h2>
            <ul className="flex flex-col gap-2 items-center md:items-start">
              {contactItems.map((c) => (
                <li key={c.text}>
                  <a href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel={c.href.startsWith("http") ? "noopener noreferrer" : undefined} className="inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm transition-colors duration-200 min-h-[44px]">
                    <span className="text-brand-red shrink-0">{c.icon}</span>
                    {c.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10 bg-slate-950/80 py-5">
        <div className="container mx-auto px-6 flex flex-col items-center gap-4 text-slate-400 text-[10px] md:text-xs md:flex-row md:justify-between md:gap-x-6 md:gap-y-2">
          <p className="text-center md:text-left">© {new Date().getFullYear()} Toraflex Colchões. Todos os direitos reservados.</p>
          <div className="flex items-center justify-center gap-x-4">
            <Link to="/politica-de-privacidade" className="hover:text-white transition-colors duration-200">Política de Privacidade</Link>
            <span className="text-white/20">|</span>
            <Link to="/termos-de-uso" className="hover:text-white transition-colors duration-200">Termos de Uso</Link>
            <span className="text-white/20">|</span>
            <Link to="/cookies" className="hover:text-white transition-colors duration-200">Cookies</Link>
          </div>
          <div className="flex items-center justify-center gap-x-3 flex-wrap">
            <a href="https://saiph.digital/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors duration-200">
              Site desenvolvido por:
              <img src={logoSaiph} alt="Saiph Digital" className="h-4 w-auto brightness-0 invert opacity-70 hover:opacity-100 transition-opacity" width={80} height={16} loading="lazy" />
            </a>
            <span className="text-white/20 hidden sm:inline">|</span>
            <a href="https://www.instagram.com/nonaka.online/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors duration-200">
              em parceria com
              <img src="/logo-nonaka.webp" alt="Nonaka" className="h-3 w-auto opacity-70 hover:opacity-100 transition-opacity" width={80} height={12} loading="lazy" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
